import { useState, useEffect } from 'react';
import { useAdminAuth } from './AdminAuthContext';
import { useAdminTheme } from './AdminThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  Users,
  Search,
  Loader2,
  UserPlus,
  Shield,
  UserCog,
  Eye,
  Mail,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const StaffManagement = () => {
  const { isSuperAdmin } = useAdminAuth();
  const { isDarkMode } = useAdminTheme();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    role: 'support_agent'
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/admins`, { withCredentials: true });
      setAdmins(response.data);
    } catch (error) {
      console.error('Error fetching admins:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view admin users');
      } else {
        toast.error('Failed to load admin users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.email || !newAdmin.role) {
      toast.error('Please fill in all fields');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/promote`, newAdmin, { withCredentials: true });
      toast.success(`User promoted to ${newAdmin.role.replace('_', ' ')}`);
      setAddModalOpen(false);
      setNewAdmin({ email: '', role: 'support_agent' });
      fetchAdmins();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to promote user';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAdmins = admins.filter(admin => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      admin.name?.toLowerCase().includes(query) ||
      admin.email?.toLowerCase().includes(query) ||
      admin.role?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-50 text-red-700 border-red-200';
      case 'support_agent': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'reviewer': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Shield className="w-4 h-4" />;
      case 'support_agent': return <UserCog className="w-4 h-4" />;
      case 'reviewer': return <Eye className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'super_admin': return 'Full access to all features';
      case 'support_agent': return 'Can view accounts, help merchants, add notes';
      case 'reviewer': return 'Can review transactions and reports';
      default: return '';
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className={`text-center py-12 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`} data-testid="admin-users-restricted">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Access Restricted</h3>
        <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Only Super Admins can manage admin users</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page" className="animate-fadeIn">
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin Users</h1>
          <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Manage administrator accounts</p>
        </div>
        <Button
          onClick={() => setAddModalOpen(true)}
          className="bg-teal-600 hover:bg-teal-700 text-white transition-all duration-200 active:scale-95"
          data-testid="add-admin-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Admin
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6 group">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors duration-200 group-focus-within:text-teal-500 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
        <Input
          placeholder="Search admins..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`pl-10 max-w-md transition-all duration-200 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
          data-testid="admin-search-input"
        />
      </div>

      {/* Role Legend */}
      <div className={`mb-6 p-4 rounded-xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Admin Roles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
              <Shield className="w-4 h-4 text-red-600" />
            </div>
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Super Admin</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Full access to all features</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <UserCog className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Support Agent</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>View accounts, help merchants, add notes</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
              <Eye className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Reviewer</p>
              <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Review transactions and reports</p>
            </div>
          </div>
        </div>
      </div>

      {/* Admins List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : filteredAdmins.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
          <Users className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
          <h3 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No admin users found</h3>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>Add your first admin user to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAdmins.map((admin, idx) => (
            <div
              key={admin.admin_id}
              className={`rounded-xl border p-5 transition-all duration-300 hover:-translate-y-1 cursor-pointer group ${isDarkMode ? 'bg-slate-800 border-slate-700 hover:border-teal-600 hover:shadow-lg' : 'bg-white border-gray-200 hover:border-teal-200 hover:shadow-lg'}`}
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`admin-card-${admin.admin_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                    admin.role === 'super_admin' ? 'bg-red-50' :
                    admin.role === 'support_agent' ? 'bg-blue-50' :
                    'bg-green-50'
                  }`}>
                    {getRoleIcon(admin.role)}
                  </div>
                  <div>
                    <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{admin.name}</h3>
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${getRoleColor(admin.role)}`}>
                      {admin.role?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                {admin.is_active ? (
                  <span className="text-emerald-500">
                    <CheckCircle2 className="w-5 h-5" />
                  </span>
                ) : (
                  <span className="text-red-500">
                    <XCircle className="w-5 h-5" />
                  </span>
                )}
              </div>
              
              <div className={`space-y-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{admin.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Added {formatDate(admin.created_at)}</span>
                </div>
              </div>
              
              <p className={`mt-3 text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                {getRoleDescription(admin.role)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Add Admin Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className={isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>Add Admin User</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>
              Promote an existing user to an admin role. The user must already have an account on the platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>User Email</label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                className={isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}
                data-testid="new-admin-email-input"
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                The user must have registered on the platform first
              </p>
            </div>
            <div>
              <label className={`text-sm mb-2 block ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>Role</label>
              <Select value={newAdmin.role} onValueChange={(value) => setNewAdmin({ ...newAdmin, role: value })}>
                <SelectTrigger className={`w-full ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-testid="new-admin-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className={isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
                  <SelectItem value="super_admin" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-500" />
                      Super Admin
                    </div>
                  </SelectItem>
                  <SelectItem value="support_agent" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4 text-blue-500" />
                      Support Agent
                    </div>
                  </SelectItem>
                  <SelectItem value="reviewer" className={isDarkMode ? 'text-slate-200' : 'text-gray-700'}>
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-green-500" />
                      Reviewer
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Role Description */}
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                {newAdmin.role === 'super_admin' && 'Super Admins have full access to all features including managing other admins, suspending merchants, and updating transaction statuses.'}
                {newAdmin.role === 'support_agent' && 'Support Agents can view merchant accounts, add support notes, and view audit logs. They cannot suspend merchants or manage transactions.'}
                {newAdmin.role === 'reviewer' && 'Reviewers can view transactions and reports, and update transaction statuses. They cannot manage merchants or other admins.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddModalOpen(false)} className={isDarkMode ? 'text-slate-400' : 'text-gray-500'}>
              Cancel
            </Button>
            <Button
              onClick={handleAddAdmin}
              disabled={!newAdmin.email || actionLoading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="confirm-add-admin-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Add Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;
